import React, { useEffect, useRef, useState } from 'react';
import { clone, cloneDeep, each } from 'lodash';
import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';
import Canvas from '../../components/canvas/index_v2';
import MaterialCanvasToolbar from '../../components/canvas/material-canvas-toolbar';
import * as appActions from '../../stores/app/actions';
import { CanvasPlaceholder } from '../../components/canvas/canvas-placeholder';
import { getDateFormat } from '../../util';
import moment from 'moment';
import request from 'superagent';
import { LinearProgress, Stack } from '@mui/material';
import { useKeycloak } from '@react-keycloak/web';
import { useAtom } from 'jotai';
import Head from 'next/head';
import Joyride from 'react-joyride';
import { HYPOTHESIS_STATES } from '../../components/canvas/constants';
import { canvasUserGuides } from '../../joyride';
import { activityAtom } from '../../stores/jotai/activities/activities';
import { baseAtom, DEFAULT_SELECTED_WEEK, params_getSlider } from '../../stores/jotai/base';
import { canvasAtom, loadLibraryExternally } from '../../stores/jotai/canvas/canvas';
import { canvasDataAtom, updatedCanvasData } from '../../stores/jotai/canvas/canvasData';
import { useGetCanvasData, useGetCanvasLayout } from '../../tanstackQuery/queries/canvas';
import isValidCanvasActivity from '../../util/isValidCanvasActivity';
import { getDateOfPeriod } from '../../util/period_utils';

const BEGINNING_DATE_LABEL = "Beginning";

interface Props {
    params?: {
        hypothesisID: string;
        chatID: string;
        entryID: string;
    },
    org?: OrgState;
    appState?: AppState;
    team?: TeamState;
    cohort?: CohortState;
    baseAtomBridge?: any;
    base: any;
    user?: User;
    canvas?: ICanvas;
    canvasEntries?: { [key: string]: CanvasEntry };
    canvasSliderValue?: string;
}

const CanvasView: React.FC<Props> = (props) => {
    const [isAddingSummary, setIsAddingSummary] = useState(false);
    const [isHidingStaleEntries, setIsHidingStaleEntries] = useState(false);
    const [isPPTDownloading, setIsPPTDownloading] = useState(false);
    const [canvasLibrary, setCanvasLibrary] = useState<any>(undefined);
    const [selectedLayout, setSelectedLayout] = useState('7bdd8112-11b4-11ed-ab60-8f1360fa7cc0');
    const [canvas_filter_showInvalidated, setCanvasFilterShowInvalidated] = useState(true);
    const [canvas_filter_showValidated, setCanvasFilterShowValidated] = useState(true);
    const [canvas_filter_showUnresolved, setCanvasFilterShowUnresolved] = useState(true);
    const [isCanvasLibraryReady,setIsCanvasLibraryReady]=useState(false);
    const [isCanvasReady,setIsCanvasReady]=useState(false);


    const {keycloak} = useKeycloak();
    const [canvasState, setCanvasState] = useAtom(canvasAtom);
    const [canvasData, setCanvasData]=useAtom(canvasDataAtom);
    const [activities,setActivities]=useAtom(activityAtom);

    const rootCanvasRef = useRef<any>(null);
    const [base, setBase] = useAtom(baseAtom);

    const {data: canvasDataAll, isLoading: isCanvasDataLoading} = useGetCanvasData({ accessToken: keycloak.token }, { groupID: props?.team?.groupID },  { enabled: !!(base?.team?.groupID) });
    const {data: canvasLayout, isLoading: isLayoutLoading}= useGetCanvasLayout({ accessToken: keycloak.token });
    useEffect(() => {
        updatedCanvasData(setCanvasData,canvasDataAll);
        loadLibraryExternally(setCanvasState,canvasLayout);

        setIsCanvasReady(canvasDataAll?.categories.length>0);
        setIsCanvasLibraryReady(canvasDataAll?.categories?.length>0 && canvasLayout?.length>0);
    }, [isCanvasDataLoading,isLayoutLoading,canvasLibrary,canvasDataAll,canvasLayout]);

    useEffect(() => {
        if (props.user) {
            appActions?.beaconReportCurrentFeature("/canvas");
        }
        
        if (canvasDataAll && canvasDataAll.description !== '') {
            setIsAddingSummary(false);
        }
    }, [canvasDataAll,isCanvasDataLoading,props.user]);

    useEffect(() => {
        rootCanvasRef.current = React.createRef();
        
        let entryFound = false;
        try{
            if (props && props.params && props.params.entryID) {
                entryFound = true;
            }
        }
        catch(e){}

        if (typeof window !== "undefined") {
            document.title = 'Canvas - Innovation Within';
        }

        loadCanvasLibrary();
    }, [canvasLayout]);


    const loadCanvasLibrary = () => {
        loadLibraryExternally(setCanvasState, canvasLayout);

        setIsCanvasLibraryReady(canvasDataAll?.categories?.length>0 && canvasLayout?.length>0)
        setCanvasLibrary(canvasLayout);
    };

    const getSelectedCanvasLayout = () => {
        let key = selectedLayout;

        if (key === undefined && canvasLibrary !== undefined) {
            key = canvasLibrary?.[0]?.canvasLayoutID;
        }

        if (key !== undefined) {
            let buffer = undefined;

            for (let i = 0; i <canvasLibrary?.length; i++) {
                if (canvasLibrary[i]?.canvasLayoutID === key) {
                    buffer = canvasLibrary[i];
                }
            }

            return buffer;
        }
        return undefined;
    }

    const isCanvasPeriodCurrent=(ignoreProgramticBlocking)=> {
        // If we're NOT ignoring programatic blocking
        if (!ignoreProgramticBlocking) {
            // Lock for situations where it should
            // be in "soft" read only
            // if (isAnyChatOpen(team)) 
                return false;
        }

        if (getIsInPeriodOrWeek() === 'period') {
            if (getSliderMax() === undefined) return false;
            if (getSliderMax() === 0) return true;

            const currentMax = getSliderMax()?.toString();
            const selectedMax = getValueForSlider()?.toString();

            if (selectedMax === currentMax) {
                return true;
            }
        }

        if (getIsInPeriodOrWeek() === 'week') {
            const currentWeek = getCurrentWeek();

            return currentWeek === -1;
        }

        return false;
    }

    const getCurrentCanvasEntries = () => {
        const {categories}=canvasDataAll

        let canvasEntries = {};
        try {

            const catKeys = Object.keys(categories);

            for (let i = 0; i < catKeys?.length; i++) {
                for (let e = 0; e <categories?.[catKeys[i]]?.entries?.length; e++) {
                    canvasEntries[
                        //@ts-ignore
                        categories?.[catKeys[i]]?.entries[e]?.entryID
                    ] =
                    {
                        //@ts-ignore
                        ...categories?.[catKeys[i]]?.entries[e],
                        //@ts-ignore
                        id: categories?.[catKeys[i]]?.entries[e]?.entryID
                    };
                }
            }
        }
        catch (e) {}

        return canvasEntries;
    }

    const shouldFilterOutHypothesis = (hypothesis) => {
        return (
            hypothesis?.state === HYPOTHESIS_STATES.DELETED ||
            hypothesis?.state?.name === HYPOTHESIS_STATES.DELETED
        );
    }

    const downloadAsPPT = () => {
        const { team, org, cohort } = props;

        setIsPPTDownloading(true);
  
        let canvasResponse = canvasDataAll;

        // Assuming `getCurrentCanvasEntries` is implemented in this functional component or passed as a prop
        const canvas_data = getCurrentCanvasEntries(); 

        let filtered_canvas_request = {
            id: canvasResponse?.canvasID,
            name: canvasResponse?.name,
            states: Object?.values(canvasResponse?.states),
            description: canvasResponse?.description,
            notes: canvasResponse?.notes,
            categories: []
        };

        each(canvasResponse?.categories, category => {
            let categoryClone = clone(category);
            categoryClone.entries = [];

            each(canvas_data, (data) => {
                if (
                    data?.categoryID === category?.categoryID &&
                    !shouldFilterOutHypothesis(data)
                ) {
                    let dataWithStateObject = cloneDeep(data);

                    if (!data?.state?.id) {
                        dataWithStateObject.state =
                        canvasDataAll?.states[
                                data?.state || "Hypothesis"
                            ];
                    }

                    categoryClone?.entries?.push(dataWithStateObject);
                }
            });

            filtered_canvas_request?.categories?.push(categoryClone);
        });

        const hrefBase = process.env.NEXT_PUBLIC_CANVAS_URI + "?groupID=";
        let pptTitle = `Org: ${org?.name}`;

        if (cohort) {
            pptTitle += ` - Cohort: ${cohort?.name}`;
        }

        if (team) {
            pptTitle += ` - Team: ${team?.name}`;
        }

        const fullRequest = `${hrefBase}${team?.id}&title=${pptTitle} Canvas&cDisplay=${props.cohort.name}`;

        request
            .post(fullRequest)
            .send(filtered_canvas_request)
            .end((err, res) => {
                const s3PPTLocation = res?.text;

                // Open in a new tab
                if (typeof window !== "undefined") {
                    window.open(s3PPTLocation, '_blank');
                }

                // End downloading state
                setIsPPTDownloading(false);
            });
    };

    // Toggle if we're hiding old invalidated entries or not
    const toggleHideStaleEntries = () => {
        setIsHidingStaleEntries((prev) => !prev);
    }

    const getValidActivityFeedLength = () => {

        if (!activities || !activities?.length) return 0;

        let count = 0;

        each(activities, feed => {

            if (isValidCanvasActivity(feed)) {
                count += 1;
            }
        });

        return count;
    };

    const getIsInPeriodOrWeek = () => {
        // When no week is selected it's in "period mode"
        if (canvasData?.selectedWeek === DEFAULT_SELECTED_WEEK) {
            return "period";
        } else {
            return "week";
        }
    }

    const getCurrentWeek = () => {
        return canvasData?.selectedWeek;
    }

    const getValueForSlider = () => {
        const { canvasSliderValue } = props;

        if (canvasSliderValue === '-1') {
            return getValidActivityFeedLength();
        }

        if (canvasSliderValue === undefined) {
            return 0;
        }
        
        return parseInt(canvasSliderValue);
    }

    const getSliderDateLabel = () => {
        const {
            // activityFeed,
            cohort
        } = props;

        if (getIsInPeriodOrWeek() === 'week') {
            const currentWeekSelection = getCurrentWeek();
            const currentWeekRange = getDateOfPeriod(props.cohort, currentWeekSelection.toString());

            return (
                cohort.periodLabel +
                " " +
                getCurrentWeek().toString() +
                " ( " +
                (currentWeekRange.start
                    ? moment(currentWeekRange.start).format(getDateFormat())
                    : BEGINNING_DATE_LABEL) +
                " - " +
                moment(currentWeekRange.end).format(getDateFormat()) +
                " )"
            );
        }

        const currentSlider = parseInt(getValueForSlider());

        let foundDate = moment()?.format(getDateFormat());

        if (isCanvasPeriodCurrent(true)) return foundDate;

        const filteredActivityFeed = activities?.filter((activity) =>
            isValidCanvasActivity(activity)
        );

        filteredActivityFeed.some((feed, index) => {
            if (currentSlider === index) {
                foundDate = moment(feed?.created)?.format(getDateFormat());
            }
        });

        return foundDate;
    }

    const getSliderMax = () => {
        const { canvasSliderMax } = params_getSlider(base,activities);

        return canvasSliderMax;
    }

    const getAlternativeCanvasType = () => {
        const {
            cohort,
            team
        } = props;

        // Prefer the cohort alternative type if it exists
        if (cohort) return cohort.alternativeCanvasType;

        if (!team) return 'flex';

        return team?.alternativeCanvasType;
    }

    if (isCanvasDataLoading || isLayoutLoading) {
        return <div style={{ width: '100%', height: '100%' }} ref={rootCanvasRef}>
            <LinearProgress className="iw-progress" />
        </div>
    }

    const renderMaterialCanvas = () => {
        // working on canvas change
        return <Canvas
            groupID={props?.team?.id}
            org={props?.org}
            user={props?.user}
            team={props?.team}
            cohort={props?.cohort}
            app={props?.appState}
            canvas={canvasData}
            layout={getSelectedCanvasLayout()}
            library={canvasLayout}
            activityFeed={activities}
            entries={getCurrentCanvasEntries()}
            isPeriodOnCurrent={true}
            isReadOnlyManual={false}
            handleCanvasChange={() => {

            }}
            chatID={undefined}
            isHidingStaleEntries={false}
            commentContextID={props?.cohort ? props?.cohort?.id : props?.team?.id}
            canvas_filter_showInvalidated={canvas_filter_showInvalidated}
            canvas_filter_showValidated={canvas_filter_showValidated}
            canvas_filter_showUnresolved={canvas_filter_showUnresolved}
        />
    }

    const {
        team,
        cohort,
        user,
        appState
    } = props;
    
    if (!isCanvasLibraryReady) {
        return <div style={{ width: '100%', height: '100%' }} ref={rootCanvasRef}>
            <LinearProgress className="iw-progress" />
        </div>
    }

    const canvasRender: JSX.Element = !isCanvasReady ? <CanvasPlaceholder /> : renderMaterialCanvas();

    return (
        <>
            <Head>
                <title>Canvas - Innovation Within</title>
            </Head>
            <div
                style={{ width: "100%", height: "100%" }}
                ref={rootCanvasRef}
            >
                <Joyride
                    steps={canvasUserGuides?.test}
                    continuous={true}
                    run={true}
                    debug={true}
                    disableScrolling={true}
                />
                <div
                    id="joy-canvas-grid"
                    style={{ backgroundColor: "transparent", height: "calc(100%)" }}
                >
                    <Stack sx={{ height: "100%" }}>
                        <MaterialCanvasToolbar
                            entries={getCurrentCanvasEntries()}
                            layout={getSelectedCanvasLayout()}
                            cohort={cohort}
                            user={user}
                            appState={appState}
                            updateCanvasLibrary={(payload) => {
                                setCanvasLibrary(payload);
                            }}
                            selectCanvasLayout={(layoutID) => {
                                setSelectedLayout(layoutID);
                            }}
                            teamID={team ? team.id : undefined}
                            alternativeCanvasType={getAlternativeCanvasType()} 
                            isPeriodCurrent={isCanvasPeriodCurrent(true)}
                            sliderMax={getSliderMax()} 
                            sliderValue={getValueForSlider()} 
                            sliderDateLabel={getSliderDateLabel()} 
                            hideSlider={getIsInPeriodOrWeek() === "week"} 
                            isPPTDownloading={isPPTDownloading}
                            canvas_filter_showInvalidated={canvas_filter_showInvalidated}
                            canvas_filter_showUnresolved={canvas_filter_showUnresolved}
                            canvas_filter_showValidated={canvas_filter_showValidated}
                            toggleCanvasFilterOption={(option: string) => {
                                if (option === "invalidated") {
                                    setCanvasFilterShowInvalidated((prev) => !prev);
                                } 
                                if (option === "unresolved") {
                                    setCanvasFilterShowUnresolved((prev) => !prev);
                                } 
                                if (option === "validated") {
                                    setCanvasFilterShowValidated((prev) => !prev);
                                }
                            }}
                            toggleHideStaleEntries={toggleHideStaleEntries} 
                            downloadAsPPT={downloadAsPPT}
                        />
                        {canvasRender}
                    </Stack>
                </div>
            </div>
        </>
    );
};

export default CanvasView;